import { useCallback, useEffect, useState, type FormEvent } from "react";
import { AppShell } from "../components/AppShell";
import { Icon } from "../components/Icon";
import { deleteCardType, fetchCatalogSnapshot, saveCardType } from "../lib/catalogApi";
import type { CardType } from "../lib/catalogTypes";

/** Card-type editor only — account types have their own savings-aware page. */
export function TypesPage() {
  const [rows, setRows] = useState<CardType[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Partial<CardType> | null>(null);
  const [busy, setBusy] = useState(false);

  const reload = useCallback(async () => {
    const snap = await fetchCatalogSnapshot();
    setRows(snap.cardTypes);
  }, []);

  useEffect(() => {
    void reload().catch((e) => setError(e instanceof Error ? e.message : "Load failed"));
  }, [reload]);

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!editing?.slug || !editing.name) return;
    setBusy(true);
    setError(null);
    try {
      await saveCardType({
        recordName: editing.recordName,
        slug: editing.slug,
        name: editing.name,
        mapsToKind: editing.mapsToKind ?? null,
        sortOrder: Number(editing.sortOrder ?? 0),
        isActive: editing.isActive !== false,
      });
      setEditing(null);
      await reload();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    } finally {
      setBusy(false);
    }
  }

  return (
    <AppShell>
      <main className="page">
        <div className="page-header">
          <div>
            <span className="overline">Catalog</span>
            <h1>Card types</h1>
            <p>Credit, debit, prepaid, … Optional mapsToKind bridges to PaymentMethodKind.</p>
          </div>
          <button
            className="btn btn-primary"
            type="button"
            onClick={() =>
              setEditing({ slug: "", name: "", mapsToKind: "", sortOrder: 0, isActive: true })
            }
          >
            <Icon name="plus" size={15} />
            Add card type
          </button>
        </div>
        {error && <div className="banner error">{error}</div>}
        {editing && (
          <form className="panel form-grid" onSubmit={(e) => void onSubmit(e)}>
            <label>
              Slug
              <input
                value={editing.slug ?? ""}
                onChange={(e) => setEditing({ ...editing, slug: e.target.value })}
                required
                disabled={Boolean(editing.recordName)}
              />
            </label>
            <label>
              Name
              <input
                value={editing.name ?? ""}
                onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                required
              />
            </label>
            <label>
              mapsToKind
              <input
                value={editing.mapsToKind ?? ""}
                onChange={(e) => setEditing({ ...editing, mapsToKind: e.target.value || null })}
                placeholder="creditCard / debitCard / cash …"
              />
            </label>
            <label>
              Sort
              <input
                type="number"
                value={editing.sortOrder ?? 0}
                onChange={(e) => setEditing({ ...editing, sortOrder: Number(e.target.value) })}
              />
            </label>
            <div className="row-actions full">
              <button className="btn btn-primary" type="submit" disabled={busy}>
                Save
              </button>
              <button className="btn btn-secondary" type="button" onClick={() => setEditing(null)}>
                Cancel
              </button>
            </div>
          </form>
        )}
        <div className="panel table-wrap">
          <table>
            <thead>
              <tr>
                <th>Name</th>
                <th>Slug</th>
                <th>mapsToKind</th>
                <th>Sort</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.recordName}>
                  <td>{row.name}</td>
                  <td>
                    <code>{row.slug}</code>
                  </td>
                  <td>
                    <code>{row.mapsToKind ?? "—"}</code>
                  </td>
                  <td>{row.sortOrder}</td>
                  <td className="row-actions">
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() => setEditing(row)}
                    >
                      Edit
                    </button>
                    <button
                      className="btn btn-secondary"
                      type="button"
                      onClick={() =>
                        void deleteCardType(row.recordName)
                          .then(reload)
                          .catch((err) =>
                            setError(err instanceof Error ? err.message : "Delete failed"),
                          )
                      }
                    >
                      Delete
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </main>
    </AppShell>
  );
}
